import React, { useState, useEffect } from "react"
import { useRiaChatContext } from "../../contexts/RiaChatContext"
import { useLocation } from "../../contexts/LocationContext"
import RiaOrb from "./RiaOrb"
import "../../styles/ria-chat.scss"

const CITY_PROMPTS = {
  Bengaluru: [
    "Find a 6-seater meeting room in HSR Layout",
    "Day pass coworking space in Koramangala",
    "Board meeting room near Koramangala",
    "Day pass office in Indiranagar under ₹500",
    "Conference room under ₹800/hour in MG Road",
    "Day pass with fast WiFi in Whitefield",
    "Virtual office address in Bengaluru for GST registration",
  ],
  Mumbai: [
    "Meeting room for 4 people in Andheri East",
    "Day pass coworking space near BKC",
    "Conference room near BKC",
    "Day pass office in Lower Parel under ₹600",
    "Meeting room under ₹800/hour in Powai",
    "Day pass with café vibe in Bandra",
    "Virtual office address in Mumbai for GST registration",
  ],
  Hyderabad: [
    "6-seater meeting room in Hitech City",
    "Day pass coworking space in Hitech City",
    "Conference room near Gachibowli",
    "Day pass office under ₹400 in Hyderabad",
    "Meeting room under ₹500/hour in Hyderabad",
    "Day pass with fast WiFi in Madhapur",
    "Virtual office address in Hyderabad for GST registration",
  ],
  Chennai: [
    "Meeting room in Perungudi for a team meeting",
    "Day pass coworking space in Perungudi",
    "Conference room near Guindy",
    "Day pass office under ₹400 in Chennai",
    "Meeting room under ₹500/hour in Chennai",
    "Day pass with quiet zone in OMR",
    "Virtual office address in Chennai for GST registration",
  ],
  Pune: [
    "Meeting room in Hinjewadi for 6 people",
    "Day pass coworking space in Hinjewadi",
    "Quiet workspace near Koregaon Park",
    "Day pass office under ₹400 in Pune",
    "Conference room under ₹500/hour in Pune",
    "Day pass with café in Baner",
    "Virtual office address in Pune for GST registration",
  ],
  Delhi: [
    "Meeting room in Connaught Place for 6 people",
    "Day pass coworking space in Connaught Place",
    "Conference room near Nehru Place",
    "Day pass office under ₹500 in Delhi",
    "Meeting room under ₹800/hour in Delhi",
    "Day pass with fast WiFi in Saket",
    "Virtual office address in Delhi for GST registration",
  ],
  Gurugram: [
    "Meeting room in Cyber City for 8 people",
    "Day pass coworking space in Cyber City",
    "Conference room near Golf Course Road, Gurugram",
    "Day pass office under ₹600 in Gurugram",
    "Meeting room under ₹800/hour in Gurugram",
    "Day pass with café in Sohna Road",
    "Virtual office address in Gurugram for GST registration",
  ],
  Noida: [
    "Meeting room in Sector 62, Noida with TV",
    "Day pass coworking space in Sector 62, Noida",
    "Conference room near Noida Expressway",
    "Day pass office under ₹400 in Noida",
    "Meeting room under ₹500/hour in Noida",
    "Day pass with quiet zone near Noida Expressway",
    "Virtual office address in Noida for GST registration",
  ],
}

const DEFAULT_PROMPTS = [
  "6-seater meeting room near me",
  "Day pass coworking space near me",
  "Conference room with projector in my city",
  "Day pass office under ₹500 near me",
  "Meeting room under ₹500/hour near me",
  "Quiet workspace with whiteboard",
  "Virtual office address for GST registration",
]

function RotatingTicker() {
  const [idx, setIdx] = useState(0)
  const { city } = useLocation()
  const prompts = CITY_PROMPTS[city] || DEFAULT_PROMPTS

  useEffect(() => {
    setIdx(0)
  }, [city])

  useEffect(() => {
    const t = setInterval(() => setIdx(v => (v + 1) % prompts.length), 3200)
    return () => clearInterval(t)
  }, [prompts.length])

  return (
    <span className="ria__bar-ticker-wrap" aria-hidden="true">
      {prompts.map((p, i) => (
        <span
          key={i}
          className={`ria__bar-ticker-item${i === idx ? " ria__bar-ticker-item--active" : ""}`}
        >
          &ldquo;{p}&rdquo;
        </span>
      ))}
    </span>
  )
}

export default function RiaFloatingButton() {
  const { isOpen, openRia, closeRia } = useRiaChatContext()

  return (
    <>
      {/* Search bar — slides down and fades out when chat opens */}
      <button
        className={`ria__search-bar${isOpen ? " ria__search-bar--gone" : ""}`}
        onClick={() => openRia()}
        aria-label="Ask Ria – GoFloaters AI space search"
        aria-hidden={isOpen}
        tabIndex={isOpen ? -1 : 0}
        type="button"
      >
        <span className="ria__search-bar-aurora" aria-hidden="true" />
        <span className="ria__search-bar-left">
          <RiaOrb size={26} />
          <span className="ria__search-bar-lead">Ask Ria</span>
          <span className="ria__search-bar-divider" aria-hidden="true" />
          <RotatingTicker />
        </span>
        <span className="ria__search-bar-cta" aria-hidden="true">
          <span>Try it</span>
          <span className="ria__search-bar-arrow">→</span>
        </span>
      </button>

      {/* Close FAB — springs in at bottom when chat is open */}
      <button
        className={`ria__close-fab${isOpen ? " ria__close-fab--visible" : ""}`}
        onClick={closeRia}
        aria-label="Close Ria"
        aria-hidden={!isOpen}
        tabIndex={isOpen ? 0 : -1}
        type="button"
      >
        <RiaOrb size={20} />
        <span className="ria__close-fab-label">Close</span>
        <span className="ria__close-fab-icon" aria-hidden="true">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </span>
      </button>
    </>
  )
}
